/**
 * Environment-based Supabase configuration.
 *
 * | Environment                     | Default DB |
 * |---------------------------------|------------|
 * | Vite dev server / localhost     | lovable    |
 * | *.lovableproject.com (preview)  | lovable    |
 * | *.lovable.app (published)       | lovable    |
 * | Custom domain (production)      | lovable    |
 *
 * An optional alternative Supabase instance (e.g. a self-hosted or staging
 * environment set up from the same migration files) can be enabled by setting:
 *
 *   VITE_SUPABASE_ALT_URL=https://<project-ref>.supabase.co
 *   VITE_SUPABASE_ALT_ANON_KEY=<anon-key>
 *   VITE_SUPABASE_ALT_LABEL=My Alt DB   (optional, defaults to "Alternative DB")
 *
 * When these variables are present the app exposes an "alt" project that can be
 * selected via Settings or ?db=alt URL parameter.
 * When published via Lovable (*.lovable.app), alt is used as the default DB.
 * Falls back to lovable if alt env vars are not configured.
 *
 * Admin can still manually override via ?db=<key> URL parameter or Settings page.
 */

export interface SupabaseProjectConfig {
  label: string;
  url: string;
  anonKey: string;
}

function buildProjects(): Record<string, SupabaseProjectConfig> {
  const projects: Record<string, SupabaseProjectConfig> = {
    lovable: {
      label: 'Lovable Cloud (Dev)',
      url: import.meta.env.VITE_SUPABASE_URL,
      anonKey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
    },
    ur_gem: {
      label: 'UR Gem (Production)',
      url: 'https://pgvrimxybgxhmkgdgscl.supabase.co',
      anonKey: 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InBndnJpbXh5Ymd4aG1rZ2Rnc2NsIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzM1MzE2MDksImV4cCI6MjA4OTEwNzYwOX0.hkTaLZBNXkZ8ywNIqXaB9gFCSYGV8r7Cswil9xFO8is',
    },
  };

  // Dynamically add alternative Supabase instance when env vars are provided.
  // This allows pointing the app at any Supabase project that has been
  // initialised with the same migration files in supabase/migrations/.
  const altUrl = import.meta.env.VITE_SUPABASE_ALT_URL;
  const altKey = import.meta.env.VITE_SUPABASE_ALT_ANON_KEY;
  if (altUrl && altKey) {
    projects['alt'] = {
      label: import.meta.env.VITE_SUPABASE_ALT_LABEL || 'Alternative DB',
      url: altUrl,
      anonKey: altKey,
    };
  }

  return projects;
}

export const SUPABASE_PROJECTS: Record<string, SupabaseProjectConfig> = buildProjects();

const STORAGE_KEY = '__supabase_active_project__';

/**
 * Determine the default project based on environment.
 * All environments default to lovable DB.
 */
function getEnvironmentDefault(): string {
  return 'lovable';
}

/**
 * Get the currently active project key.
 * Priority: URL param (?db=lovable) > localStorage > environment default.
 * URL param also persists the choice to localStorage for subsequent loads.
 */
export function getActiveProjectKey(): string {
  // 1. URL parameter override (highest priority)
  try {
    const params = new URLSearchParams(window.location.search);
    const dbParam = params.get('db');
    if (dbParam && SUPABASE_PROJECTS[dbParam]) {
      // Persist to localStorage so it sticks after the URL param is removed
      localStorage.setItem(STORAGE_KEY, dbParam);
      return dbParam;
    }
  } catch {
    // URL parsing unavailable
  }

  // 2. localStorage override
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && SUPABASE_PROJECTS[stored]) {
      return stored;
    }
  } catch {
    // localStorage unavailable
  }

  // 3. Environment default
  return getEnvironmentDefault();
}

/**
 * Get the active Supabase config (URL + anonKey).
 */
export function getActiveConfig(): SupabaseProjectConfig {
  return SUPABASE_PROJECTS[getActiveProjectKey()];
}

/**
 * Check if the current selection is a manual override (differs from env default).
 */
export function isManualOverride(): boolean {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored !== null && stored !== getEnvironmentDefault();
  } catch {
    return false;
  }
}

/**
 * Reset to environment default (remove manual override).
 */
export function resetToDefault(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // localStorage unavailable
  }
  window.location.reload();
}

/**
 * Switch the active Supabase project and reload the page.
 */
export function setActiveProject(key: string): void {
  if (!SUPABASE_PROJECTS[key]) {
    console.error(`[SupabaseConfig] Unknown project key: ${key}`);
    return;
  }
  try {
    // If selecting the environment default, remove override
    if (key === getEnvironmentDefault()) {
      localStorage.removeItem(STORAGE_KEY);
    } else {
      localStorage.setItem(STORAGE_KEY, key);
    }
  } catch {
    // localStorage unavailable
  }
  window.location.reload();
}
